/**
 * Chores & Rewards API.
 *
 * **Nothing here turns a star into money.** A money reward is a reward with a price, like a
 * trip to the bookshop — the screen says plainly why a rate from stars to cash stops working
 * once children expect it.
 *
 * The three tabs come in one request because the balances on the Rewards tab are worked out
 * from the chores on the Today tab. Asked separately they would briefly disagree, and a child
 * reading two different numbers for their own stars is the end of the feature.
 */

import { api } from './client.js';

export async function board() {
  return api.get('/chores/');
}

export async function addChore(fields) {
  return api.post('/chores/chores/', fields);
}

/**
 * Take a chore off the chart.
 *
 * **Retired, not deleted.** `Done` cascades, so deleting a chore would wipe the thirty-one
 * days a child kept it up — and the streak is the part they care about. A retired chore
 * stops appearing and keeps everything it earned.
 */
export async function retireChore(choreId, on) {
  return api.patch(`/chores/chores/${choreId}/`, { retired_on: on });
}

/** Tick it off, or take it back. The same call both ways — a child taps the wrong line. */
export async function tickChore(choreId) {
  return api.post(`/chores/chores/${choreId}/done/`, {});
}

export async function addReward(fields) {
  return api.post('/chores/rewards/', fields);
}

/**
 * Take a reward off the list.
 *
 * **Retired, not deleted.** A reward that has ever been claimed is part of what a child
 * already got, and `Claim` protects it — deleting one answered with a 500 rather than
 * admitting the history was in the way.
 */
export async function retireReward(rewardId, on) {
  return api.patch(`/chores/rewards/${rewardId}/`, { retired_on: on });
}

/** A child asking. **It spends nothing** — the stars come off when a parent says yes. */
export async function claimReward(rewardId, who) {
  return api.post(`/chores/rewards/${rewardId}/claim/`, { who });
}

export async function answerClaim(claimId, yes, because = '') {
  return api.post(`/chores/claims/${claimId}/answer/`, { yes, because });
}

/** Stars on or off by hand. The reason is required, so a child can read why. */
export async function adjustStars(fields) {
  return api.post('/chores/adjust/', fields);
}

/** What a child is putting their stars towards. One at a time. */
export async function saveFor(who, reward) {
  return api.post('/chores/saving-for/', { who, reward });
}
